// Global console capture system - centralized initialization
declare global {
  interface Window {
    consoleCapture: {
      logs: Array<{
        type: string;
        timestamp: string;
        message: string;
      }>;
      originalMethods: Record<string, Function>;
      hasErrors: boolean;
      initialized?: boolean;
    };
    copyAllConsole: () => Promise<void>;
    testConsoleCapture: () => string;
    showConsoleAlert?: (message: string) => void;
  }
}

// Only initialize if not already done
if (!window.consoleCapture || !window.consoleCapture.initialized) {
  window.consoleCapture = {
    logs: [],
    originalMethods: {},
    hasErrors: false,
    initialized: true,
  };

  // Store and override console methods only once
  (['log', 'error', 'warn', 'info', 'debug'] as const).forEach(method => {
    window.consoleCapture.originalMethods[method] = console[method];
    console[method] = function (...args: any[]) {
      // Suppress known third-party warnings
      if (method === 'error' || method === 'warn') {
        const message = args[0];
        // Suppress react-beautiful-dnd warnings
        if (
          typeof message === 'string' &&
          (message.includes('Support for defaultProps will be removed from memo components') ||
            message.includes('react-beautiful-dnd') ||
            message.includes('Unable to find draggable with id'))
        ) {
          return; // Suppress these warnings completely
        }
      }

      // Call original method first
      window.consoleCapture.originalMethods[method].apply(console, args);

      // Track if we have errors
      if (method === 'error') {
        window.consoleCapture.hasErrors = true;
      }

      // Store the output
      window.consoleCapture.logs.push({
        type: method.toUpperCase(),
        timestamp: new Date().toISOString(),
        message: args
          .map(arg => (typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)))
          .join(' '),
      });

      // Limit stored logs to prevent memory issues (keep last 1000)
      if (window.consoleCapture.logs.length > 1000) {
        window.consoleCapture.logs = window.consoleCapture.logs.slice(-1000);
      }
    };
  });
}

// Make test functions available globally for debugging
window.testConsoleCapture = function () {
  console.log('Testing console capture system...');
  console.warn('This is a test warning');
  console.error('This is a test error to trigger the copy button');
  console.info('Console capture test completed');
  return 'Test console messages generated';
};

// Copy function with URL info and filtering options
window.copyAllConsole = async function (errorsOnly = false) {
  const currentUrl = window.location.href;
  const referrer = document.referrer || 'Direct navigation';
  const timestamp = new Date().toISOString();

  // Filter logs based on preference (errors only or all)
  const filteredLogs = errorsOnly
    ? window.consoleCapture.logs.filter(log => log.type === 'ERROR' || log.type === 'WARN')
    : window.consoleCapture.logs;

  // Create header with context information
  const header = `=== CONSOLE OUTPUT CAPTURED ===
This console was copied from: ${currentUrl}
Referrer page: ${referrer}
Captured at: ${timestamp}
Filter: ${errorsOnly ? 'Errors and Warnings Only' : 'All Console Messages'}
Total messages: ${filteredLogs.length}
${errorsOnly ? `(${window.consoleCapture.logs.length - filteredLogs.length} other messages filtered out)` : ''}
=======================================

`;

  const logOutput = filteredLogs
    .map(log => `[${log.timestamp}] ${log.type}: ${log.message}`)
    .join('\n');

  const output = header + logOutput;

  try {
    await navigator.clipboard.writeText(output);
    // Use original console method to avoid potential loops
    window.consoleCapture.originalMethods.log('Console output copied to clipboard!');

    // Show user feedback
    const message = errorsOnly
      ? `${filteredLogs.length} errors/warnings copied to clipboard!`
      : `${filteredLogs.length} console messages copied to clipboard!`;

    if (window.showConsoleAlert) {
      window.showConsoleAlert(message);
    } else {
      alert(message);
    }
  } catch (err) {
    window.consoleCapture.originalMethods.error('Copy failed:', err);
    alert('Failed to copy console output');
  }
};

// Global error handler for JavaScript errors
window.addEventListener('error', event => {
  window.consoleCapture.hasErrors = true;

  const errorDetails = {
    message: event.message,
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
    error: event.error ? event.error.toString() : null,
    stack: event.error ? event.error.stack : null,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    url: window.location.href,
  };

  // Use original console to avoid loops
  window.consoleCapture.originalMethods.error('Global JavaScript Error:', errorDetails);
});

// Handle unhandled promise rejections
window.addEventListener('unhandledrejection', function (event) {
  window.consoleCapture.hasErrors = true;
  // Use original console to avoid loops
  window.consoleCapture.originalMethods.error('Unhandled promise rejection:', event.reason);
});

export {};
